import { ChevronDown, Share04 } from "@untitledui/icons";
import type { FC, HTMLAttributes, MouseEventHandler, ReactNode } from "react";
import { Link as AriaLink } from "react-aria-components";
import { Badge } from "@/components/base/badges/badges";
import { cx, sortCx } from "@/utils/cx";

const styles = sortCx({
	root: "group relative flex w-full cursor-pointer items-center rounded-md bg-primary outline-focus-ring transition duration-100 ease-linear select-none hover:bg-primary_hover focus-visible:z-10 focus-visible:outline-2 focus-visible:outline-offset-2",
	rootSelected:
		"bg-gray-200 hover:bg-neutral-200 dark:bg-neutral-600 dark:hover:bg-neutral-600",
});

type NavItemBaseProps = {
	iconOnly?: boolean;
	open?: boolean;
	href?: string;
	type: "link" | "collapsible" | "collapsible-child";
	icon?: FC<HTMLAttributes<HTMLOrSVGElement>>;
	iconClassName?: string;
	badge?: ReactNode;
	current?: boolean;
	truncate?: boolean;
	onClick?: MouseEventHandler;
	children?: ReactNode;
};

export const NavItemBase = ({
	current,
	type,
	badge,
	href,
	icon: Icon,
	iconClassName,
	children,
	truncate = true,
	onClick,
}: NavItemBaseProps) => {
	const iconElement = Icon && (
		<Icon
			aria-hidden="true"
			className={cx(
				"mr-2 size-5 shrink-0 text-fg-quaternary transition-inherit-all",
				iconClassName
			)}
		/>
	);

	const badgeElement =
		badge && (typeof badge === "string" || typeof badge === "number") ? (
			<Badge className="ml-3" color="gray" size="sm" type="pill-color">
				{badge}
			</Badge>
		) : (
			badge
		);

	const labelElement = (
		<span
			className={cx(
				"flex-1 font-semibold text-md text-secondary transition-inherit-all group-hover:text-secondary_hover",
				truncate && "truncate",
				current && "text-secondary_hover"
			)}
		>
			{children}
		</span>
	);

	const isExternal = href && href.startsWith("http");
	const externalIcon = isExternal && (
		<Share04 className="size-4 stroke-[2.5px] text-fg-quaternary" />
	);

	if (type === "collapsible") {
		return (
			<summary
				className={cx("px-3 py-2", styles.root, current && styles.rootSelected)}
				onClick={onClick}
			>
				{iconElement}

				{labelElement}

				{badgeElement}

				<ChevronDown
					aria-hidden="true"
					className="in-open:-scale-y-100 ml-3 size-4 shrink-0 stroke-[2.5px] text-fg-quaternary"
				/>
			</summary>
		);
	}

	if (type === "collapsible-child") {
		if (!href && onClick) {
			return (
				<button
					aria-label={typeof children === "string" ? children : undefined}
					className={cx(
						"py-2 pr-3 pl-10 text-left",
						styles.root,
						current && styles.rootSelected
					)}
					onClick={onClick}
					tabIndex={0}
					type="button"
				>
					{labelElement}
					{badgeElement}
				</button>
			);
		}

		return (
			<AriaLink
				aria-current={current ? "page" : undefined}
				className={cx(
					"py-2 pr-3 pl-10",
					styles.root,
					current && styles.rootSelected
				)}
				href={href ?? "#"}
				onClick={onClick}
				rel="noopener noreferrer"
				target={isExternal ? "_blank" : "_self"}
			>
				{labelElement}
				{externalIcon}
				{badgeElement}
			</AriaLink>
		);
	}

	return (
		<AriaLink
			aria-current={current ? "page" : undefined}
			className={cx("px-3 py-2", styles.root, current && styles.rootSelected)}
			href={href!}
			onClick={onClick}
			rel="noopener noreferrer"
			target={isExternal ? "_blank" : "_self"}
		>
			{iconElement}
			{labelElement}
			{externalIcon}
			{badgeElement}
		</AriaLink>
	);
};
